<template>
  <div v-if="travelersAll.length > 0" id="list" :class="{ wizard: isWizard }" class="form-group">
    <h4>Traveler List</h4>
    <p class="infomsg">In most cases, only your own traveler information needs to be entered below. This information is used for trip bookings. In rare cases, you may add your family, friends or people external to your organization, if you plan to book travel for them under your account.</p>
    <p v-if="isWizard" class="check">
      Please select traveler/s you are booking for this trip or
      <a @click="$emit('update:isForm', true)">add a new traveler</a>.
    </p>
    <div v-if="isWizard && travelersAll.length > 2" class="form-row">
      <input id="search" v-model="search" class="search" name="search" type="text" placeholder="Search for a traveler by name..." />
    </div>
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>{{ isWizard ? "Creator" : "Passport" }}</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="t in travelersFiltered" :key="t.id">
          <td v-if="isWizard" class="checkboxlabel" :class="{ on: check(t) }" @click="update(t)">
            <div class="checkbox" />
            <label>{{ t.passport_first_name }} {{ t.passport_last_name }}</label>
          </td>
          <td v-else>{{ t.passport_first_name }} {{ t.passport_last_name }}</td>
          <td v-if="isWizard">
            {{ t.customer_name }}
          </td>
          <td v-else>
            {{ t.passport_number ? t.passport_number : "No passport" }}
          </td>
          <td class="actions">
            <span v-if="t.customer_id == userId">
              <a v-if="!t.is_me" class="you" @click="actionYou(t.id)">Set as You</a>
              <span v-if="t.is_me" class="you">You</span>
              <a @click="actionEdit(t.id)">Edit</a>
            </span>
            <span v-else class="unauth">Contact creator to edit</span>
            <a @click="actionDelete(t.id, t.customer_id)">Delete</a>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="travelersAll.length == 0" class="table-msg">No items</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject } from "vue";
import useApi from "../../utils/apiCalls.js";

const emit = defineEmits(["error", "update:editId", "update:isForm", "update:isNoPax", "update:travelers", "scroll"]);
const props = defineProps({
  editId: [String, Number],
  isForm: Boolean,
  isNoPax: Boolean,
  isWizard: Boolean,
  newId: [String, Number],
  travelers: Array,
});

const userId = inject("userId");

const isLoading = ref(true);
const search = ref(null);
const travelersAll = ref([]);
const travelersSelected = ref(props.travelers);

const { apiGetRequest, apiDeleteRequest, apiPutRequest } = useApi(emit);

const load = () => {
  isLoading.value = true;

  const qs = props.isWizard ? "?all=true" : "?all=false";
  apiGetRequest({
    path: `/travelers${qs}`,
    onSuccess: data => {
      travelersAll.value = data;
      if (props.isWizard && travelersAll.value.length == 0) {
        emit("update:isForm", true);
        emit("update:isNoPax", true);
      }
      isLoading.value = false;
    },
    onError: error => {
      isLoading.value = false;
      emit("error", error);
    },
  });
};

const travelersFiltered = computed(() => {
  let filteredAndSorted = travelersAll.value;

  if (search.value !== null) {
    filteredAndSorted = travelersAll.value.filter(traveler => {
      return traveler.passport_first_name.toLowerCase().includes(search.value.toLowerCase());
    });
  }

  // Sort function to rank is_me first
  filteredAndSorted.sort((a, b) => {
    const aIsOwn = a.customer_id == userId.value;
    const aIsMe = a.is_me === true;

    const bIsOwn = b.customer_id == userId.value;
    const bIsMe = b.is_me === true;

    if (aIsOwn && aIsMe && (!bIsOwn || !bIsMe)) return -1;
    if (bIsOwn && bIsMe && (!aIsOwn || !aIsMe)) return 1;

    // Otherwise order by name
    return a.passport_first_name.toLowerCase() < b.passport_first_name.toLowerCase() ? -1 : 1;
  });

  return filteredAndSorted;
});

const update = traveler => {
  if (check(traveler)) {
    travelersSelected.value = travelersSelected.value.filter(trav => trav.id != traveler.id);
  } else {
    const mname = traveler.passport_middle_name ? traveler.passport_middle_name + " " : "";
    travelersSelected.value.push({
      id: traveler.id,
      name: (traveler.passport_first_name + " " + mname + traveler.passport_last_name).replace(/\s+/g, " ").trim(),
    });
  }
  emit("update:travelers", travelersSelected.value);
};

const check = traveler => {
  return travelersSelected.value.some(t => t.id == traveler.id);
};

const actionDelete = (id, customerId) => {
  const msg = customerId == userId.value ? "Are you sure you want to delete this traveler?" : "Are you sure you want to delete this traveler? You did NOT create this traveler, but we allow deletion to remove duplicates. Please only continue if you are sure this traveler should be deleted.";

  if (confirm(msg)) {
    isLoading.value = true;
    apiDeleteRequest({
      path: `/travelers/${id}`,
      onSuccess: () => {
        load();
      },
      onError: error => {
        isLoading.value = false;
        emit("error", error);
      },
    });
  }
};

const actionEdit = id => {
  emit("update:editId", id);

  setTimeout(() => {
    const el = document.getElementById("form");
    if (el == null) return;
    el.scrollIntoView({ behavior: "smooth" });
  }, 500);
};

const actionYou = id => {
  isLoading.value = true;
  apiPutRequest({
    path: `/travelers/${id}/default`,
    onSuccess: () => {
      load();
    },
    onError: error => {
      isLoading.value = false;
      emit("error", error);
    },
  });
};

watch(
  () => props.editId,
  val => {
    if (val != null) {
      // Scroll into view
      var el = document.getElementById("form");
      el.scrollIntoView({ behavior: "smooth" });
      load();
    }
  }
);

watch(
  () => props.newId,
  val => {
    if (val != null) load();
  }
);

onMounted(load);
</script>

<style lang="less">
#list {
  &.wizard {
    .check {
      margin-bottom: 1rem;
    }

    .checkboxlabel label {
      display: inline-block;
    }
  }

  .you,
  .unauth {
    display: none;
  }
}

@media screen and (min-width: 60em) {
  #list {
    .you,
    .unauth {
      display: inline-block;
    }
  }
}
</style>
